import { ReactElement, useCallback, useId } from 'react'

import { ImportedInvoiceReviewFormSchema } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'

import { useFormContext, useWatch } from '../../../../../../../../../../../../hooks/useForm'
import { ImportedInvoiceReviewCurrencySelect } from './elements/ImportedInvoiceReviewCurrencySelect'
import * as Styled from './styles'

type AmountFieldName = `lines.${number}.amountExclVat`

interface ImportedInvoiceReviewAmountFieldProps {
  isReadOnly: boolean
  label: string
  name: AmountFieldName
  withCurrencyPicker: boolean
}

/**
 * An amount joined to the invoice currency picker, standing in for the Figma `InputGroup` until
 * Lumen provides one.
 *
 * Currency is one invoice field shown on every amount. It is never registered as a form field, so
 * every picker mirrors the form value and writes a pick back to it.
 */
export const ImportedInvoiceReviewAmountField = ({
  isReadOnly,
  label,
  name,
  withCurrencyPicker,
}: ImportedInvoiceReviewAmountFieldProps): ReactElement => {
  const { control, formState, FormItem, setValue } = useFormContext<ImportedInvoiceReviewFormSchema>()
  const currency = useWatch({ control, name: 'currency' })
  const currencySelectId = useId()

  const isDisabled = isReadOnly || formState.isSubmitting
  const isCurrencyDisabled = isDisabled || !withCurrencyPicker
  const isCurrencyInvalid = Boolean(formState.errors.currency)

  const handleCurrencySelect = useCallback(
    (currencyId?: string) => {
      setValue('currency', currencyId ?? null, { shouldDirty: true, shouldValidate: true })
    },
    [setValue],
  )

  return (
    <FormItem
      label={label}
      name={name}
      render={({ field: { ref, value, ...field } }) => (
        <Styled.ImportedInvoiceReviewAmountFieldWrapper>
          {/* Without `isFallbackToZero` the empty field would read as a real `0.00` amount. */}
          <Styled.AmountInput
            {...field}
            disabled={isDisabled}
            isFallbackToZero={false}
            onClear={() => field.onChange(null)}
            value={value ?? undefined}
          />
          <ImportedInvoiceReviewCurrencySelect
            disabled={isCurrencyDisabled}
            error={isCurrencyInvalid}
            id={currencySelectId}
            onSelect={handleCurrencySelect}
            selectedId={currency ?? undefined}
          />
        </Styled.ImportedInvoiceReviewAmountFieldWrapper>
      )}
    />
  )
}
